import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { fetchAnalyticsReports, type AnalyticsReports } from "../api/analyticsReports";
import type { PeriodDays } from "../api/dashboard";

// ─── Colours ──────────────────────────────────────────────────────────────────

const SOURCE_COLOURS: Record<string, string> = {
  booking_page: "#64748B",
  web_widget: "#C9734F",
  voice: "#22C55E",
  instagram: "#A855F7",
  facebook: "#3B82F6",
  sms: "#D4A63D",
  whatsapp: "#10B981",
};
const FALLBACK_COLOUR = "#CBD5E1";

function sourceColour(channelKey: string, channelLabel: string): string {
  return SOURCE_COLOURS[channelKey] ?? SOURCE_COLOURS[channelLabel] ?? FALLBACK_COLOUR;
}

// ─── Currency formatter ───────────────────────────────────────────────────────

function fmtCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ─── Custom tooltips ──────────────────────────────────────────────────────────

interface TooltipEntry {
  dataKey?: string | number;
  name?: string | number;
  color?: string;
  fill?: string;
  value?: string | number;
}

interface DarkTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
  currency: string;
}

const DarkTooltip = ({ active, payload, label, currency }: DarkTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ backgroundColor: "#0F172A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", p: 1.5, minWidth: 140 }}>
      {label && <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mb: 0.5 }}>{label}</Typography>}
      {payload.map((entry) => (
        <Box key={entry.dataKey ?? entry.name} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ fontSize: "0.78rem", color: entry.color ?? entry.fill ?? "#fff" }}>{entry.name}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#fff", fontWeight: 600 }}>
            {entry.dataKey === "revenue" || entry.name === "Revenue"
              ? fmtCurrency(Number(entry.value), currency)
              : entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── Custom pie label ─────────────────────────────────────────────────────────

interface PieLabelProps {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
}

const PieLabel = ({
  cx = 0,
  cy = 0,
  midAngle = 0,
  innerRadius = 0,
  outerRadius = 0,
  percent = 0,
}: PieLabelProps) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 11, fontWeight: 700 }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ─── Chart card wrapper ───────────────────────────────────────────────────────

function ChartCard({ title, loading, children, minH = 280 }: { title: string; loading: boolean; children: React.ReactNode; minH?: number }) {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #E2E8F0", backgroundColor: "#fff" }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#0F172A", mb: 2.5 }}>{title}</Typography>
      {loading ? (
        <Skeleton variant="rectangular" height={minH} sx={{ borderRadius: "8px" }} />
      ) : (
        children
      )}
    </Paper>
  );
}

// ─── Empty chart state ────────────────────────────────────────────────────────

function EmptyChart({ height = 240 }: { height?: number }) {
  return (
    <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ fontSize: "0.85rem", color: "#94A3B8" }}>No data for this period</Typography>
    </Box>
  );
}

function fmtRate(value: number | null | undefined) {
  return value === null || value === undefined ? "—" : `${value}%`;
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const [period, setPeriod] = useState<PeriodDays>(30);
  const [data, setData] = useState<AnalyticsReports | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchAnalyticsReports(period)
      .then(setData)
      .catch((e) => {
        console.error("Analytics reports fetch failed:", e);
        setError("Could not load analytics data.");
      })
      .finally(() => setLoading(false));
  }, [period]);

  const currency = data?.currency ?? "USD";

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <BarChartRoundedIcon sx={{ color: "#C9734F", fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F172A" }}>
            Analytics
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, v) => v && setPeriod(v as PeriodDays)}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              border: "1px solid #E2E8F0",
              color: "#64748B",
              fontWeight: 600,
              fontSize: "0.78rem",
              textTransform: "none",
              px: 2,
              py: 0.6,
              "&.Mui-selected": {
                backgroundColor: "#C9734F",
                color: "#fff",
                borderColor: "#C9734F",
                "&:hover": { backgroundColor: "#b8613f" },
              },
              "&:hover": { backgroundColor: "#F8FAFC" },
            },
          }}
        >
          <ToggleButton value={7}>7d</ToggleButton>
          <ToggleButton value={30}>30d</ToggleButton>
          <ToggleButton value={90}>90d</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", border: "1px solid #FEE2E2", backgroundColor: "#FFF5F5", mb: 3 }}>
          <Typography sx={{ color: "#EF4444", fontWeight: 500, textAlign: "center" }}>{error}</Typography>
        </Paper>
      )}

      <Grid container spacing={2.5}>
        {/* ── Booking Sources (Pie) ── */}
        <Grid item xs={12} md={5}>
          <ChartCard title="Booking Sources" loading={loading} minH={300}>
            {!data?.booking_sources?.length ? (
              <EmptyChart height={300} />
            ) : (
              <Box>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={data.booking_sources}
                      dataKey="bookings"
                      nameKey="channel"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      labelLine={false}
                      label={<PieLabel />}
                    >
                      {data.booking_sources.map((entry) => (
                        <Cell key={entry.channel_key} fill={sourceColour(entry.channel_key, entry.channel)} />
                      ))}
                    </Pie>
                    <Tooltip content={<DarkTooltip currency={currency} />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Legend */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "center", mt: 1 }}>
                  {data.booking_sources.map((entry) => (
                    <Box key={entry.channel_key} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: sourceColour(entry.channel_key, entry.channel), flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>
                        {entry.channel} ({entry.bookings})
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </ChartCard>
        </Grid>

        {/* ── Revenue by Service (Horizontal Bar) ── */}
        <Grid item xs={12} md={7}>
          <ChartCard title="Revenue by Service" loading={loading} minH={300}>
            {!data?.revenue_by_service?.length ? (
              <EmptyChart height={300} />
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(240, data.revenue_by_service.length * 44)}>
                <BarChart
                  data={data.revenue_by_service}
                  layout="vertical"
                  margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
                  barSize={18}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: "#94A3B8" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => fmtCurrency(v, currency)}
                  />
                  <YAxis
                    type="category"
                    dataKey="service_name"
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    axisLine={false}
                    tickLine={false}
                    width={110}
                  />
                  <Tooltip content={<DarkTooltip currency={currency} />} />
                  <Bar dataKey="revenue" name="Revenue" fill="#C9734F" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        {/* ── Bookings by Day of Week ── */}
        <Grid item xs={12}>
          <ChartCard title="Bookings by Day of Week" loading={loading} minH={200}>
            {!data?.bookings_by_day?.some((d) => d.bookings > 0) ? (
              <EmptyChart height={200} />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.bookings_by_day} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<DarkTooltip currency={currency} />} />
                  <Bar dataKey="bookings" name="Bookings" fill="#C9734F" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12}>
          <ChartCard title="Source Performance" loading={loading} minH={180}>
            {!data?.source_performance?.length ? (
              <EmptyChart height={180} />
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ minWidth: 620 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1fr", gap: 1.5, pb: 1, borderBottom: "1px solid #E2E8F0" }}>
                    {["Source", "Conversations", "Bookings", "Revenue", "Conversion"].map((label) => (
                      <Typography key={label} sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748B" }}>{label}</Typography>
                    ))}
                  </Box>
                  {data.source_performance.map((row) => (
                    <Box key={row.channel_key} sx={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1fr", gap: 1.5, py: 1.1, borderBottom: "1px solid #F1F5F9" }}>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#0F172A" }}>{row.channel}</Typography>
                      <Typography sx={{ fontSize: "0.82rem", color: "#475569" }}>{row.conversations ?? "—"}</Typography>
                      <Typography sx={{ fontSize: "0.82rem", color: "#475569" }}>{row.bookings}</Typography>
                      <Typography sx={{ fontSize: "0.82rem", color: "#475569" }}>{fmtCurrency(row.revenue, currency)}</Typography>
                      <Typography sx={{ fontSize: "0.82rem", color: "#475569" }}>{fmtRate(row.conversion_rate)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <ChartCard title="Conversation Sentiment" loading={loading} minH={240}>
            {!data?.conversation_sentiment?.length ? (
              <EmptyChart height={240} />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {data.conversation_sentiment.map((row) => (
                  <Box key={row.sentiment} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.8, borderBottom: "1px solid #F1F5F9" }}>
                    <Typography sx={{ fontSize: "0.84rem", color: "#0F172A", fontWeight: 600 }}>{row.label}</Typography>
                    <Typography sx={{ fontSize: "0.84rem", color: "#475569" }}>{row.count}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            aria-disabled="true"
            sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "action.disabledBackground",
              minHeight: 240,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "text.disabled" }}>
                Missed Opportunities
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.disabled" }}>
                Coming soon
              </Typography>
            </Box>
            <Box sx={{ minHeight: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontSize: "0.85rem", color: "text.disabled", textAlign: "center" }}>
                Recoverable booking opportunities will appear here.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={0}
            aria-disabled="true"
            sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "action.disabledBackground",
              minHeight: 220,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "text.disabled" }}>
                Service Interest vs Bookings
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.disabled" }}>
                Coming soon
              </Typography>
            </Box>
            <Box sx={{ minHeight: 140, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontSize: "0.85rem", color: "text.disabled", textAlign: "center" }}>
                Advanced service analytics will appear here.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
